import { useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";

import type { AdminDailyWatch, AdminDanceSummary } from "@recital-master/shared";
import { danceStyleLabel } from "@recital-master/shared";

import { fmtFamilies, fmtHours, fmtPlays } from "@/components/admin/adminFormat";
import { FamilyWatchBreakdown } from "@/components/admin/FamilyWatchBreakdown";
import { WatchMomentSparkline } from "@/components/admin/RetentionCurve";
import { useAdminDailyFamilyPlays } from "@/data/adminDashboard";
import { Card } from "@/design/primitives";
import { Muted, Text } from "@/design/Text";
import { fonts, palette, radius, space } from "@/design/tokens";
import { displayTitle } from "@/lib/format";

const TRACK_HEIGHT = 84;
const TOOLTIP_WIDTH = 175;
/** Enough of a fill that a day with a few minutes still reads as a day. */
const MIN_FILL_HEIGHT = 3;

/**
 * A YYYY-MM-DD calendar date is not an instant. Anchor it at local noon so no
 * timezone can drag the label back a day.
 */
function calendarDate(date: string): Date {
  return new Date(`${date}T12:00:00`);
}

function axisLabel(date: string): string {
  return calendarDate(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
}

function dayLabel(date: string, index: number, total: number): string {
  if (index === total - 1) return "Today";
  if (index === total - 2) return "Yesterday";
  return calendarDate(date).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric"
  });
}

function daySummary(day: AdminDailyWatch): string {
  return day.watchHours > 0
    ? [
        `${fmtHours(day.watchHours)} watched`,
        day.playCount == null ? null : fmtPlays(day.playCount),
        fmtFamilies(day.familyCount)
      ].filter(Boolean).join(" · ")
    : "Nothing watched";
}

function timeLabel(iso: string | null): string {
  if (!iso) return "Unknown time";
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit"
  });
}

function fullDateLabel(date: string): string {
  return calendarDate(date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
}

function DailyFamilyDances({
  compact,
  date,
  fallbackDances,
  familyAccountId
}: {
  compact: boolean;
  date: string;
  fallbackDances: AdminDanceSummary[];
  familyAccountId: string;
}) {
  const {data, error, loading} = useAdminDailyFamilyPlays(
    familyAccountId,
    date,
    fallbackDances
  );
  if (loading && !data) {
    return <Muted variant="small">Loading individual plays…</Muted>;
  }
  if (error && !data) {
    return <Muted variant="small">Individual play details could not be loaded.</Muted>;
  }
  if (!data || data.dances.length === 0) {
    return <Muted variant="small">No qualifying dance watches were recorded.</Muted>;
  }
  return (
    <View style={styles.dailyDances}>
      <Text variant="overline" color={palette.muted}>
        {`Dances watched on ${fullDateLabel(date)}`}
      </Text>
      <View style={styles.dailyDanceList}>
        {data.dances.map((dance, index) => (
          <View
            key={`${dance.recitalId}/${dance.performanceId}/${dance.danceId}`}
            style={[
              styles.dailyDanceGroup,
              index > 0 && styles.dailyDanceDivider
            ]}
          >
            <View style={styles.dailyDanceIdentityRow}>
              <Text variant="bodyStrong" numberOfLines={1}>
                {displayTitle(dance.title)}
              </Text>
              <Muted variant="small" numberOfLines={1}>
                {dance.performanceTitle}
                {danceStyleLabel(dance) ? ` · ${danceStyleLabel(dance)}` : ""}
              </Muted>
            </View>
            <View style={styles.dailyPlaybackList}>
              {dance.playbacks.map((playback, playIndex) => (
                <View
                  key={playback.playId}
                  style={[
                    styles.dailyPlaybackRow,
                    compact && styles.dailyPlaybackRowCompact,
                    playIndex > 0 && styles.dailyPlaybackDivider
                  ]}
                >
                  <View style={styles.dailyPlaybackMeta}>
                    <Text variant="smallStrong" color={palette.textSoft}>
                      {timeLabel(playback.lastWatchedAt)}
                    </Text>
                    <Muted variant="small">
                      {`${fmtHours(playback.watchedSeconds / 3600)} watched`}
                    </Muted>
                  </View>
                  <WatchMomentSparkline
                    accessibilityLabel={`${displayTitle(dance.title)}, playback at ${timeLabel(playback.lastWatchedAt)}, ${fmtHours(playback.watchedSeconds / 3600)} watched`}
                    buckets={playback.buckets}
                    durationSeconds={dance.durationSeconds}
                    plays={1}
                    storyboardVttUrl={dance.storyboardVttUrl}
                  />
                </View>
              ))}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

/**
 * Daily watch hours across the trend window. A single day compared against
 * yesterday is noise — morning always loses to a finished day — so the shape
 * of the run is the honest comparison. Today is the lit bar on the right.
 */
export function DailyWatchTrend({
  compact,
  days
}: {
  compact: boolean;
  days: AdminDailyWatch[];
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);

  if (days.length === 0) return null;

  const peakHours = Math.max(...days.map((day) => day.watchHours));
  const readoutIndex = selected ?? days.length - 1;
  const readout = days[readoutIndex];

  return (
    <Card>
      <View style={styles.readoutRow}>
        <Text variant="smallStrong" color={palette.textSoft}>
          {dayLabel(readout.date, readoutIndex, days.length)}
          <Muted variant="small">
            {`  ·  ${daySummary(readout)}`}
          </Muted>
        </Text>
      </View>
      <View style={[styles.barRow, styles.baseline]}>
        {days.map((day, index) => {
          // Selection owns the readout and family list. Hover is deliberately
          // transient: it only reveals this bar's tooltip.
          const lit = index === readoutIndex;
          const fillRatio = peakHours > 0 ? day.watchHours / peakHours : 0;
          const fillHeight = fillRatio > 0
            ? Math.max(MIN_FILL_HEIGHT, fillRatio * TRACK_HEIGHT)
            : 0;
          return (
            <Pressable
              accessibilityRole="adjustable"
              accessibilityLabel={`${dayLabel(day.date, index, days.length)}, ${
                day.watchHours > 0
                  ? [
                      `${fmtHours(day.watchHours)} watched`,
                      day.playCount == null ? null : fmtPlays(day.playCount),
                      fmtFamilies(day.familyCount)
                    ].filter(Boolean).join(", ")
                  : "nothing watched"
              }`}
              key={day.date}
              onHoverIn={() => setHovered(index)}
              onHoverOut={() => setHovered(null)}
              // Pressing locks a day so its household detail remains visible
              // after the pointer leaves the bar.
              onPress={() => setSelected(index)}
              style={[
                styles.barColumn,
                hovered === index && styles.barColumnHovered
              ]}
            >
              {hovered === index ? (
                <View
                  pointerEvents="none"
                  style={[
                    styles.tooltip,
                    { bottom: fillHeight + space.sm },
                    index < 2
                      ? styles.tooltipLeft
                      : index > days.length - 3
                        ? styles.tooltipRight
                        : styles.tooltipCenter
                  ]}
                >
                  <Text variant="smallStrong" style={styles.tooltipTitle}>
                    {dayLabel(day.date, index, days.length)}
                  </Text>
                  {day.watchHours > 0 ? (
                    <>
                      <Text variant="smallStrong" color={palette.textSoft}>
                        {fmtHours(day.watchHours)} watched
                      </Text>
                      <Muted variant="small">
                        {[
                          day.playCount == null ? null : fmtPlays(day.playCount),
                          fmtFamilies(day.familyCount)
                        ].filter(Boolean).join(" · ")}
                      </Muted>
                    </>
                  ) : (
                    <Muted variant="small">Nothing watched</Muted>
                  )}
                </View>
              ) : null}
              {fillRatio > 0 ? (
                <View
                  style={[
                    styles.barFill,
                    {
                      height: fillHeight,
                      opacity: lit ? 1 : 0.4
                    }
                  ]}
                />
              ) : null}
            </Pressable>
          );
        })}
      </View>
      <View style={styles.axisRow}>
        <Muted variant="small">{axisLabel(days[0].date)}</Muted>
        <Muted variant="small">
          {peakHours > 0 ? `Peak ${fmtHours(peakHours)}` : "No watching yet"}
        </Muted>
        <Muted variant="small">Today</Muted>
      </View>
      <FamilyWatchBreakdown
        key={readout.date}
        compact={compact}
        rows={(readout.families ?? []).map((family) => {
          const lastWatchedAt = family.dances?.reduce<string | null>(
            (latest, dance) =>
              dance.lastWatchedAt && (!latest || dance.lastWatchedAt > latest)
                ? dance.lastWatchedAt
                : latest,
            null
          ) ?? null;
          return {
            key: family.familyAccountId,
            email: family.email,
            summary: [
              `${fmtHours(family.watchHours)} watched`,
              fmtPlays(family.playCount),
              lastWatchedAt ? `Last at ${timeLabel(lastWatchedAt)}` : null
            ].filter(Boolean).join(" · "),
            expandedContent: (
              <DailyFamilyDances
                compact={compact}
                date={readout.date}
                fallbackDances={family.dances ?? []}
                familyAccountId={family.familyAccountId}
              />
            )
          };
        })}
      />
    </Card>
  );
}

const styles = StyleSheet.create({
  readoutRow: {
    marginBottom: space.md,
    minHeight: 18
  },
  barRow: {
    alignItems: "flex-end",
    flexDirection: "row",
    gap: space.xs,
    height: TRACK_HEIGHT
  },
  /** Bars need a floor to sit on; without it short days look like they float. */
  baseline: {
    borderBottomColor: palette.hairline,
    borderBottomWidth: 1
  },
  barColumn: {
    cursor: "pointer",
    flex: 1,
    height: "100%",
    justifyContent: "flex-end",
    minWidth: 0,
    position: "relative"
  },
  barColumnHovered: {
    zIndex: 3
  },
  barFill: {
    backgroundColor: palette.accent,
    // Square feet, softened caps: a two-minute day should read as a sliver,
    // not a lozenge.
    borderTopLeftRadius: radius.sm / 2,
    borderTopRightRadius: radius.sm / 2,
    width: "100%"
  },
  axisRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: space.sm
  },
  tooltip: {
    backgroundColor: palette.surfaceOverlay,
    borderColor: palette.hairline,
    borderRadius: radius.md,
    borderWidth: 1,
    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.34)",
    gap: 1,
    paddingHorizontal: space.sm,
    paddingVertical: space.sm,
    position: "absolute",
    width: TOOLTIP_WIDTH
  },
  tooltipTitle: {
    fontFamily: fonts.display,
    fontSize: 14,
    fontWeight: "900",
    lineHeight: 19
  },
  tooltipLeft: {
    left: 0
  },
  tooltipCenter: {
    left: "50%",
    transform: [{ translateX: -TOOLTIP_WIDTH / 2 }]
  },
  tooltipRight: {
    right: 0
  },
  dailyDances: {
    gap: space.md
  },
  dailyDanceList: {
    backgroundColor: palette.surface,
    borderColor: palette.hairline,
    borderRadius: radius.sm,
    borderWidth: 1,
    overflow: "hidden"
  },
  dailyDanceGroup: {
    padding: space.md
  },
  dailyDanceDivider: {
    borderTopColor: palette.hairline,
    borderTopWidth: 1
  },
  dailyDanceIdentityRow: {
    gap: 2,
    marginBottom: space.sm,
    minWidth: 0
  },
  dailyPlaybackList: {
    gap: 0
  },
  dailyPlaybackRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: space.lg,
    minHeight: 46,
    paddingVertical: space.xs
  },
  dailyPlaybackRowCompact: {
    alignItems: "stretch",
    flexDirection: "column",
    gap: space.sm
  },
  dailyPlaybackDivider: {
    borderTopColor: palette.hairline,
    borderTopWidth: 1
  },
  dailyPlaybackMeta: {
    flexShrink: 0,
    width: 128
  }
});
